import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Flashcard, UserProgress, StudyTechnique } from '../../types';
import { SpacedRepetition } from '../../utils/spacedRepetition';
import { Flashcard as FlipCard } from '../Flashcard';

const StudyContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '16px',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: '24px',
  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
  backdropFilter: 'blur(18px)',
  minHeight: 'calc(100vh - 140px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    minHeight: 'calc(100vh - 120px)',
  },
}));

interface InterleavingProps {
  flashcards: Flashcard[];
  userProgress: UserProgress[];
  onProgressUpdate: (progress: UserProgress) => void;
  onSessionComplete: (sessionData: any) => void;
}

export const Interleaving: React.FC<InterleavingProps> = ({
  flashcards,
  userProgress,
  onProgressUpdate,
  onSessionComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffledCards, setShuffledCards] = useState<Flashcard[]>([]);
  const [startTime] = useState(Date.now());
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);

  // Initialize shuffled cards from different topics
  useEffect(() => {
    const interleavedCards = createInterleavedSet(flashcards);
    setShuffledCards(interleavedCards);
  }, [flashcards]);

  const createInterleavedSet = (cards: Flashcard[]): Flashcard[] => {
    // Group cards by topic
    const byTopic: Record<string, Flashcard[]> = {};
    cards.forEach(card => {
      if (!byTopic[card.topic]) byTopic[card.topic] = [];
      byTopic[card.topic].push(card);
    });

    // Interleave cards from different topics
    const interleaved: Flashcard[] = [];
    const topicKeys = Object.keys(byTopic);
    const maxLength = Math.max(...topicKeys.map(key => byTopic[key].length));

    for (let i = 0; i < maxLength; i++) {
      topicKeys.forEach(topic => {
        if (byTopic[topic][i]) {
          interleaved.push(byTopic[topic][i]);
        }
      });
    }

    return interleaved.slice(0, 20); // Limit to 20 cards for interleaving practice
  };

  const progress = shuffledCards.length > 0 ? ((currentIndex + 1) / shuffledCards.length) * 100 : 0;
  const currentCard = shuffledCards[currentIndex];

  useEffect(() => {
    if (sessionComplete) {
      const sessionData = {
        technique: StudyTechnique.INTERLEAVING,
        flashcardsStudied: shuffledCards.length,
        correctAnswers,
        totalTime: Math.floor((Date.now() - startTime) / 60000),
        accuracy: shuffledCards.length > 0 ? (correctAnswers / shuffledCards.length) * 100 : 0
      };
      onSessionComplete(sessionData);
    }
  }, [sessionComplete, correctAnswers, shuffledCards.length, startTime, onSessionComplete]);

  const handleAnswer = (quality: number) => {
    if (!currentCard) return;

    const currentProgress = userProgress.find(p => p.flashcardId === currentCard.id);
    if (currentProgress) {
      const updatedProgress = SpacedRepetition.calculateNextReview(currentProgress, quality);
      onProgressUpdate(updatedProgress);
    }

    if (quality >= 3) {
      setCorrectAnswers(prev => prev + 1);
    }
    if (currentIndex < shuffledCards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setSessionComplete(true);
    }
  };

  if (sessionComplete) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Dialog open={true} maxWidth="sm" fullWidth>
            <DialogTitle>Interleaving Session Complete!</DialogTitle>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Session Summary
              </Typography>
              <Typography>Flashcards studied: {shuffledCards.length}</Typography>
              <Typography>Correct answers: {correctAnswers}</Typography>
              <Typography>Accuracy: {shuffledCards.length > 0 ? ((correctAnswers / shuffledCards.length) * 100).toFixed(1) : 0}%</Typography>
              <Typography>Time spent: {Math.floor((Date.now() - startTime) / 60000)} minutes</Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Interleaving helps build connections between different topics and improves long-term retention!
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => window.location.reload()}>Start New Session</Button>
            </DialogActions>
          </Dialog>
        </StudyContainer>
      </motion.div>
    );
  }

  if (!currentCard) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Typography>Preparing interleaved study session...</Typography>
          <Typography variant="body2" color="text.secondary">
            Mixing cards from different topics for better learning connections.
          </Typography>
        </StudyContainer>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      <StudyContainer>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Interleaving
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Card {currentIndex + 1} of {shuffledCards.length}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />
        </Box>

        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          <FlipCard flashcard={currentCard} studyMode onAnswer={handleAnswer} />
        </Box>
      </StudyContainer>
    </motion.div>
  );
};
